/**
 * Time helpers for the coordination workspace (M36-T07). Data, not components,
 * so they live outside the page file (fast refresh) and the tests import them.
 */
import type { QueueItem } from './api/emergencyCoordination'

/** The queue and the switch re-fetch this often (spec §C.2). */
export const REFETCH_MS = 30_000

/** The spec says "end of the current shift"; 8 hours stands in until AMTC's
 * shifts are known. */
export const DEFAULT_ON_HOURS = 8

export function minutesSince(iso: string, now: number): number {
  return Math.floor((now - new Date(iso).getTime()) / 60_000)
}

/** "4 min ago", "2 h ago", "3 days ago". */
export function ageLabel(iso: string, now: number): string {
  const mins = minutesSince(iso, now)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} h ago`
  const days = Math.floor(hours / 24)
  return `${days} ${days === 1 ? 'day' : 'days'} ago`
}

/** Unacknowledged (ADR-0065 decision 4) and older than the API's threshold. */
export function isWaiting(
  item: QueueItem,
  warnMinutes: number,
  now: number,
): boolean {
  return (
    item.acknowledged_at === null &&
    minutesSince(item.created_at, now) >= warnMinutes
  )
}

/** A `datetime-local` value (local time, no zone) for a Date. */
export function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
